/**
 * Guided product tour provider (QA F4, founder decision 2026-10-16; reverses
 * BACKLOG D6, which had retired the auto-start).
 *
 * - Auto-starts once, on the user's first arrival in the app after onboarding
 *   or checkout (never on gated/checkout routes), while `/auth/me` reports
 *   `guided_tour_seen_at: null`. Finishing or skipping writes the server flag,
 *   so it never replays on another device.
 * - Replayable from the Help menu (shell) and from Profile.
 * - Steps depend on the user's role, derived from the same flags that gate
 *   the navigation (`tour/tourModel.ts`).
 * - Also owns the dismissed state of the contextual "what is this?" hints.
 *
 * The pure model (steps, reducer, auto-start rule) lives in tour/tourModel.ts;
 * the overlay is components/tutorial/TutorialLayer.tsx.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { usePathname } from 'expo-router';

import { MCS_ENABLED } from '../config/featureFlags';
import { useTabAccess } from '../hooks/useTabAccess';
import { useResponsive } from '../hooks/useResponsive';
import { authAPI } from '../services/api';
import {
  trackHintOpened,
  trackTourCompleted,
  trackTourReplayed,
  trackTourSkipped,
  trackTourStarted,
} from '../services/analytics';
import {
  INITIAL_TOUR_STATE,
  buildTourSteps,
  currentTourStep,
  isHintVisible as isHintVisibleFor,
  resolveTourRole,
  shouldAutoStart,
  tourReducer,
  type GuidanceHintId,
  type TourAccessFlags,
  type TourRole,
  type TourSource,
  type TourStepDef,
} from '../tour/tourModel';
import { useAuth } from './AuthContext';

/** Let the landing screen render before the tour dims it. */
const AUTO_START_DELAY_MS = 1200;

export interface TutorialContextValue {
  isActive: boolean;
  role: TourRole;
  source: TourSource | null;
  steps: TourStepDef[];
  currentStep: TourStepDef | null;
  currentStepIndex: number;
  totalSteps: number;
  /** Path the tour started from (the layer returns there on finish). */
  returnTo: string | null;
  isPhone: boolean;
  startTour: (source: Exclude<TourSource, 'auto'>) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTutorial: () => void;
  /** Kept for callers of the old tutorial API: replays the tour from Profile. */
  resetTutorial: () => Promise<void>;
  isHintVisible: (hintId: GuidanceHintId) => boolean;
  openHint: (hintId: GuidanceHintId) => void;
  dismissHint: (hintId: GuidanceHintId) => void;
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

export function TutorialProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { phase, user, billingStatus, setUser } = useAuth();
  const { planTier, isMentorOrAdmin, isB2BUser, showProgress } = useTabAccess();
  const { isPhone } = useResponsive();

  const showMentorEngagements = planTier === 'tier3' || Boolean(isMentorOrAdmin);
  const flags = useMemo<TourAccessFlags>(() => ({
    showMentorEngagements,
    showProgress: Boolean(showProgress),
    showMcs: MCS_ENABLED && showMentorEngagements,
    isB2BUser: Boolean(isB2BUser),
    isMentorOrAdmin: Boolean(isMentorOrAdmin),
    courseAccessActive: Boolean(billingStatus?.course_access_active),
  }), [billingStatus?.course_access_active, isB2BUser, isMentorOrAdmin, showMentorEngagements, showProgress]);
  const role = resolveTourRole(flags);

  const [state, dispatch] = useReducer(tourReducer, INITIAL_TOUR_STATE);
  const handledThisSession = useRef(false);
  const runningRef = useRef(false);
  runningRef.current = state.status === 'running';
  const [localDismissedHints, setLocalDismissedHints] = useState<string[]>([]);

  // Logging out ends any tour; a different user gets a fresh session.
  useEffect(() => {
    if (phase !== 'authenticated') {
      dispatch({ type: 'reset' });
      handledThisSession.current = false;
      setLocalDismissedHints([]);
    }
  }, [phase, user?.id]);

  const begin = useCallback((source: TourSource) => {
    const steps = buildTourSteps(role, flags);
    // A second start while a tour is on screen is ignored (and not tracked).
    if (steps.length === 0 || runningRef.current) return;
    handledThisSession.current = true;
    dispatch({ type: 'start', source, role, steps, returnTo: pathname || null });
    trackTourStarted(role, source, steps.length);
    if (source !== 'auto') trackTourReplayed(source);
  }, [flags, pathname, role]);

  // ── Auto-start once ──
  const seenAt = user ? (user as { guided_tour_seen_at?: string | null }).guided_tour_seen_at : undefined;
  const autoStartReady = shouldAutoStart({
    phase,
    pathname,
    seenAt,
    handledThisSession: handledThisSession.current,
    isActive: state.status === 'running',
  });
  useEffect(() => {
    if (!autoStartReady) return;
    const timer = setTimeout(() => {
      if (!handledThisSession.current) begin('auto');
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [autoStartReady, begin]);

  // ── Finish: persist "seen" server-side, then forget the run ──
  useEffect(() => {
    if (state.status !== 'done') return;
    const { outcome, role: runRole, source, stepId, stepIndex, stepCount } = state;
    if (outcome === 'completed') {
      trackTourCompleted(runRole, source, stepCount);
    } else {
      trackTourSkipped(stepId, stepIndex, runRole, source);
    }
    const alreadySeen = Boolean(seenAt);
    if (!alreadySeen && user) {
      setUser({ ...user, guided_tour_seen_at: new Date().toISOString() } as typeof user);
    }
    authAPI.markGuidedTourSeen(outcome).catch((error) => {
      // The session guard still prevents a replay until the next load.
      console.warn('[tour] could not save the tour state:', (error as Error)?.name);
    });
    dispatch({ type: 'reset' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  const currentStep = currentTourStep(state);
  const running = state.status === 'running' ? state : null;

  const nextStep = useCallback(() => dispatch({ type: 'next' }), []);
  const prevStep = useCallback(() => dispatch({ type: 'back' }), []);
  const skipTutorial = useCallback(() => dispatch({ type: 'skip' }), []);
  const startTour = useCallback((source: Exclude<TourSource, 'auto'>) => {
    if (phase !== 'authenticated') return;
    begin(source);
  }, [begin, phase]);
  const resetTutorial = useCallback(async () => startTour('profile'), [startTour]);

  // ── Contextual hints ──
  const serverDismissedHints = (user as { dismissed_hints?: string[] } | null)?.dismissed_hints;
  const isHintVisible = useCallback(
    (hintId: GuidanceHintId) => isHintVisibleFor(hintId, serverDismissedHints, localDismissedHints),
    [localDismissedHints, serverDismissedHints],
  );
  const openHint = useCallback((hintId: GuidanceHintId) => trackHintOpened(hintId), []);
  const dismissHint = useCallback((hintId: GuidanceHintId) => {
    setLocalDismissedHints((current) => (current.includes(hintId) ? current : [...current, hintId]));
    authAPI.dismissGuidanceHint(hintId).catch((error) => {
      console.warn('[hint] could not save the dismissal:', (error as Error)?.name);
    });
  }, []);

  const value = useMemo<TutorialContextValue>(() => ({
    isActive: Boolean(running),
    role: running?.role ?? role,
    source: running?.source ?? null,
    steps: running?.steps ?? [],
    currentStep,
    currentStepIndex: running?.index ?? 0,
    totalSteps: running?.steps.length ?? 0,
    returnTo: running?.returnTo ?? null,
    isPhone,
    startTour,
    nextStep,
    prevStep,
    skipTutorial,
    resetTutorial,
    isHintVisible,
    openHint,
    dismissHint,
  }), [
    currentStep,
    dismissHint,
    isHintVisible,
    isPhone,
    nextStep,
    openHint,
    prevStep,
    resetTutorial,
    role,
    running,
    skipTutorial,
    startTour,
  ]);

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
}

const noop = () => {};

const defaultContextValue: TutorialContextValue = {
  isActive: false,
  role: 'coach',
  source: null,
  steps: [],
  currentStep: null,
  currentStepIndex: 0,
  totalSteps: 0,
  returnTo: null,
  isPhone: false,
  startTour: noop,
  nextStep: noop,
  prevStep: noop,
  skipTutorial: noop,
  resetTutorial: async () => {},
  isHintVisible: () => false,
  openHint: noop,
  dismissHint: noop,
};

export function useTutorial(): TutorialContextValue {
  return useContext(TutorialContext) ?? defaultContextValue;
}

/** Whether a specific tour step is on screen. */
export function useTutorialStep(stepId: string): boolean {
  const { currentStep, isActive } = useTutorial();
  return isActive && currentStep?.id === stepId;
}
